// /settings/usage (docs/09 §4 Usage row; usage.md): the org-wide ACTIVITY
// page the sidebar usage row links to. Settings archetype, config container.
// A UTC calendar-month period picker (`?period=YYYY-MM`), the three activity
// counters full-size, two modest breakdowns (by event type, by agent — both
// existing `metrics:query` group-bys), and the owner/admin-gated signed
// usage-report download (`POST /v1/system/usage-export`; the gate is
// server-side — /v1/me carries no role — so the action always renders and a
// PERMISSION_DENIED envelope surfaces inline). Hosted installs additionally
// render the entitlement projection; self-managed installs remain activity-only.

import { useMemo } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { getRuntimeConfig } from "../config";
import { apiClient } from "../lib/apiClient";
import {
  creditUsageState,
  formatCredits,
  getBillingEntitlement,
  humanizePlanKey,
  useCreditBilling,
} from "../lib/billing";
import type { WidgetData } from "../lib/dashboardData";
import { toWidget, useMetric } from "../lib/dashboardData";
import { categoryFromGroups, formatUnits } from "../lib/dashboardMetrics";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import {
  parseUsageSettingsSearch,
  type UsageSettingsSearch,
} from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import {
  currentMonthKey,
  monthLabel,
  monthWindowFromKey,
  recentMonthKeys,
  usageCountersFromResponse,
} from "../lib/usageActivity";
import {
  withWorkingScopeSearch,
  type WorkingScopeSearch,
} from "../lib/workingScope";
import {
  Button,
  Grid,
  GridItem,
  Inline,
  InlineError,
  Page,
  Progress,
  Section,
  Select,
  Skeleton,
  Stack,
  StatCard,
  type ChartDatum,
} from "../ui";
import { Chart } from "../ui";
import { WidgetFrame } from "./MetricWidgetFrame";

type UsageExportResponse = components["schemas"]["UsageExportResponse"];
type UsageSettingsSearchState = UsageSettingsSearch & WorkingScopeSearch;

// The period picker's depth: the current month plus the eleven before it.
const PERIOD_MONTH_COUNT = 12;

// Browser-download of the signed report document (the DataTable CSV-export
// precedent): the verbatim `{payload, signature}` JSON, named like the CLI's
// `--out usage-<month>.marker-usage.json` (docs/08 §XIV).
function saveUsageReport(report: UsageExportResponse, periodKey: string) {
  const blob = new Blob([JSON.stringify(report, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `usage-${periodKey}.marker-usage.json`;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function UsageSettings() {
  const navigate = useTypedNavigate<UsageSettingsSearchState>();
  const search = useTypedSearch(
    withWorkingScopeSearch(parseUsageSettingsSearch),
  );

  // Frozen per mount so options and the default stay stable across renders;
  // a month rollover mid-session refreshes on the next full load.
  const defaultPeriod = useMemo(() => currentMonthKey(), []);
  const periodOptions = useMemo(
    () =>
      recentMonthKeys(PERIOD_MONTH_COUNT).map((key) => ({
        key,
        label: monthLabel(key),
      })),
    [],
  );
  const selectedPeriod = search.period ?? defaultPeriod;
  const window = useMemo(
    () => monthWindowFromKey(selectedPeriod),
    [selectedPeriod],
  );

  // ONE by-event-type read feeds both the counters and the event-type
  // breakdown; the per-agent breakdown retains event_type so quantities with
  // different units never share a total.
  const byEventType = useMetric({
    bucket: "none",
    group_by: ["event_type"],
    metric: "usage_units",
    window,
  });
  const byAgent = useMetric({
    bucket: "none",
    group_by: ["event_type", "agent_id"],
    metric: "usage_units",
    window,
  });
  const creditBilling = useCreditBilling();
  const entitlement = useQuery({
    enabled: creditBilling.enabled,
    queryKey: queryKeys.billingEntitlement,
    queryFn: getBillingEntitlement,
  });

  const download = useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST("/v1/system/usage-export", {
        body: {
          key_id: "installation",
          period_end: window.end,
          period_start: window.start,
          signing_key_ref: getRuntimeConfig().usageExportSigningKeyRef,
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: (report) => {
      saveUsageReport(report, selectedPeriod);
    },
  });

  // Functional-merge navigation ONLY: patch `period` over the previous
  // canonical scope search. The default (current) month is written as
  // `undefined` so it stays out of the URL.
  const onSelectPeriod = (key: string) => {
    void navigate({
      search: mergeSearch<UsageSettingsSearchState>({
        period: key === defaultPeriod ? undefined : key,
      }),
    });
  };

  const counters = useMemo<UsageSettingsCounter[]>(() => {
    if (!byEventType.data) {
      return [];
    }
    return usageCountersFromResponse(byEventType.data);
  }, [byEventType.data]);

  return (
    <UsageSettingsView
      byAgent={toWidget(
        byAgent,
        (response) => categoryFromGroups(response, "quantity"),
        [],
      )}
      byEventType={toWidget(
        byEventType,
        (response) => categoryFromGroups(response, "quantity"),
        [],
      )}
      counters={counters}
      countersError={
        byEventType.isError ? requestErrorDescription(byEventType.error) : null
      }
      downloadError={
        download.isError ? requestErrorDescription(download.error) : null
      }
      isCountersLoading={byEventType.isPending}
      isEntitlementLoading={
        creditBilling.isPending ||
        (creditBilling.enabled && entitlement.isPending)
      }
      entitlement={
        entitlement.data && !entitlement.isError ? entitlement.data : null
      }
      entitlementError={
        entitlement.isError ? requestErrorDescription(entitlement.error) : null
      }
      isDownloadPending={download.isPending}
      onDownload={() => {
        download.mutate();
      }}
      onSelectPeriod={onSelectPeriod}
      periodOptions={periodOptions}
      selectedPeriod={selectedPeriod}
    />
  );
}

export interface UsageSettingsCounter {
  id: string;
  label: string;
  quantity: number;
}

export interface UsageSettingsPeriodOption {
  key: string;
  label: string;
}

export interface UsageSettingsViewProps {
  byAgent: WidgetData<ChartDatum[]>;
  byEventType: WidgetData<ChartDatum[]>;
  counters: readonly UsageSettingsCounter[];
  countersError: string | null;
  downloadError: string | null;
  entitlement: Awaited<ReturnType<typeof getBillingEntitlement>> | null;
  entitlementError: string | null;
  isCountersLoading: boolean;
  isDownloadPending: boolean;
  isEntitlementLoading: boolean;
  onDownload: () => void;
  onSelectPeriod: (key: string) => void;
  periodOptions: readonly UsageSettingsPeriodOption[];
  selectedPeriod: string;
}

export function UsageSettingsView({
  byAgent,
  byEventType,
  counters,
  countersError,
  downloadError,
  entitlement,
  entitlementError,
  isCountersLoading,
  isDownloadPending,
  isEntitlementLoading,
  onDownload,
  onSelectPeriod,
  periodOptions,
  selectedPeriod,
}: UsageSettingsViewProps) {
  return (
    <Page variant="config">
      <Stack gap={6}>
        <Inline align="end" gap={3}>
          <Select
            label="Period"
            size="sm"
            value={selectedPeriod}
            onChange={(event) => {
              onSelectPeriod(event.currentTarget.value);
            }}
          >
            {periodOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </Select>
          <Button
            disabled={isDownloadPending}
            variant="secondary"
            onClick={onDownload}
          >
            {isDownloadPending
              ? "Preparing report…"
              : "Download signed usage report"}
          </Button>
        </Inline>
        {downloadError ? <InlineError>{downloadError}</InlineError> : null}
        {isEntitlementLoading ? (
          <Section heading="Billing">
            <Skeleton label="Loading billing" rows={2} shape="detail-field" />
          </Section>
        ) : null}
        {entitlementError ? (
          <Section heading="Billing">
            <InlineError>{entitlementError}</InlineError>
          </Section>
        ) : null}
        {entitlement ? <UsageEntitlement entitlement={entitlement} /> : null}
        <Section heading="Activity">
          {isCountersLoading ? (
            <Skeleton
              label="Loading usage activity"
              rows={3}
              shape="detail-field"
            />
          ) : null}
          {countersError ? <InlineError>{countersError}</InlineError> : null}
          {!isCountersLoading && countersError === null ? (
            <Grid columns={3} gap={6}>
              {counters.map((counter) => (
                <Stack gap={2} key={counter.id}>
                  <StatCard
                    label={counter.label}
                    value={formatUnits(counter.quantity)}
                  />
                </Stack>
              ))}
            </Grid>
          ) : null}
        </Section>
        <Grid columns={2} gap={6}>
          <GridItem>
            <WidgetFrame
              emptyMessage="No usage recorded in this period."
              heading="Usage by event type"
              isEmpty={byEventType.data.length === 0}
              notice={byEventType.notice}
              state={byEventType}
            >
              <Chart
                ariaLabel="Usage quantity by event type"
                data={byEventType.data}
                valueFormatter={formatUnits}
                variant="bar"
              />
            </WidgetFrame>
          </GridItem>
          <GridItem>
            <WidgetFrame
              emptyMessage="No usage recorded in this period."
              heading="Usage by event type and agent"
              isEmpty={byAgent.data.length === 0}
              notice={byAgent.notice}
              state={byAgent}
            >
              <Chart
                ariaLabel="Usage quantity by event type and agent"
                data={byAgent.data}
                valueFormatter={formatUnits}
                variant="top-list"
              />
            </WidgetFrame>
          </GridItem>
        </Grid>
      </Stack>
    </Page>
  );
}

function UsageEntitlement({
  entitlement,
}: {
  entitlement: Awaited<ReturnType<typeof getBillingEntitlement>>;
}) {
  const balance = Math.max(entitlement.credit_balance, 0);
  const allotment = entitlement.credit_allotment;
  const usageState = creditUsageState(balance, allotment);
  const hasAllotment = allotment != null && allotment > 0;

  return (
    <Section heading="Billing">
      <Stack gap={3}>
        <StatCard
          label="Plan"
          value={
            entitlement.plan_key
              ? humanizePlanKey(entitlement.plan_key)
              : "No active plan"
          }
        />
        {usageState === "exhausted" ? (
          <InlineError>
            Limit reached — you&apos;ve used your full monthly credit allotment.
          </InlineError>
        ) : null}
        {usageState === "warn_80" ? (
          <InlineError>
            Almost out — you&apos;ve used 80% of your monthly credits.
          </InlineError>
        ) : null}
        <StatCard
          label="Credit balance"
          value={`${formatCredits(balance)} credits remaining`}
        />
        {hasAllotment ? (
          <Stack gap={2}>
            <Progress
              label="Credit balance versus monthly allotment"
              max={allotment}
              value={balance}
            />
            <span>
              {formatCredits(balance)} of {formatCredits(allotment)} monthly
              credits remaining.
            </span>
          </Stack>
        ) : (
          <span>Monthly credit allotment unavailable.</span>
        )}
      </Stack>
    </Section>
  );
}
